import { useState, useRef, useEffect, useMemo } from "react";

import moment from "moment-timezone";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Bar, Chart } from "react-chartjs-2";

//Backend access
import { loadToken, getCameraHistory } from 'services/api/bks';
import PropTypes from "prop-types";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import CameraImage from "components/Camera/CameraImage";
import { Box, Card, Icon, Slider, ThemeProvider } from "@mui/material";

import ClearwayOverlay from '../../../../assets/images/clearway_overlay.png';
import { createTheme } from '@mui/material/styles';
import SoftTypography from "components/SoftComponents/SoftTypography";

import theme from "assets/theme";
import LiveViewDisplay from "components/LiveView/display";
import SoftButton from "components/SoftComponents/SoftButton";
const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;


const sliderTheme = createTheme({
    palette: {
        primary: {
            light: '#757ce8',
            main: '#3f50b5',
            dark: '#002884',
            contrastText: '#fff',
        },
        secondary: {
            light: '#ff7961',
            main: '#f44336',
            dark: '#ba000d',
            contrastText: '#000',
        },
    },
});

function MobileDirectionalPad({ onMove, isLoading, currentTime, valueLabelFormat, handleChange, startTime, endTime, deviceLayout }) {
    // Common style for all directional buttons
    const buttonStyles = {
        width: "60px",
        height: "30px",
        minWidth: "60px",
        borderRadius: "8px", // Matches Soft UI "lg"
        padding: 0,
        fontSize: "1.5rem",
    };

    return (
        <SoftBox py={1}
            display="flex"
            justifyContent="center"
            sx={{
                width: "100%",
                margin: "0 auto",
                touchAction: "none" // Prevents browser scroll while clicking
            }}
        >
            <SoftBox display="flex" justifyContent="center" width="100%" sx={{ gap: 2 }}>
                <SoftButton variant="gradient" color="info" sx={buttonStyles} onClick={() => onMove("left")} disabled={isLoading}>
                    <Icon>keyboard_arrow_left</Icon>
                </SoftButton>
                <SoftBox display="flex" justifyContent="center" alignItems="center" sx={{ height: '100%', width: "100%" }}>
                    <ThemeProvider theme={sliderTheme}>
                        {isLoading ? (
                            <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} disabled />
                        ) : (
                            <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime}
                                sx={{
                                    '& .MuiSlider-valueLabel': {
                                        zIndex: 100000000
                                    },
                                }}
                            />
                        )}
                    </ThemeProvider>
                </SoftBox>
                <SoftButton variant="gradient" color="info" sx={buttonStyles} onClick={() => onMove("right")} disabled={isLoading}>
                    <Icon>keyboard_arrow_right</Icon>
                </SoftButton>
            </SoftBox>
        </SoftBox>
    );
}

// Images
function LiveviewFeed({ uuid, currentTime, setCurrentTime, startTime, setStartTime, endTime, setEndTime, liveviewData, isLoading, current_liveview_url, device, deviceLayout, isPerspectiveLiveview, setIsPerspectiveLiveview, directionTrigger }) {
    const [selectedImage, setSelectedImage] = useState({
        index: liveviewData[Number(liveviewData.length) - 1] ? Number(liveviewData.length) - 1 : -1,
        image: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].image_hash : null,
        time_displayed: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].time_displayed : moment().subtract(1, 'day').unix(),
        time_changed: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].time_changed : moment().unix()
    });

    loadToken(useAuth0());

    function nextImage(currentTime, key_used = false) {
        var now_time = false;
        var new_image = {
            index: selectedImage.index,
            image: selectedImage.image_hash,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        }

        if (key_used) {
            new_image.index++;
        }
        else {
            for (var image_index in liveviewData) {
                if (currentTime >= liveviewData[image_index].time_displayed && currentTime <= liveviewData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || (liveviewData.length && new_image.index >= liveviewData.length)) {
            if (new_image.index >= liveviewData.length) {
                var now_time = true;
            }
            new_image.index = liveviewData.length - 1;
        }

        if (typeof liveviewData != 'undefined' && typeof liveviewData[new_image.index] != 'undefined') {
            new_image.image = liveviewData[new_image.index].image_hash;
            new_image.time_displayed = liveviewData[new_image.index].time_displayed;
            new_image.time_changed = liveviewData[new_image.index].time_changed;
        }

        if (key_used) {
            if (now_time) {
                setCurrentTime(endTime);
            }
            else {
                setCurrentTime(Number(new_image.time_displayed) + 1);
            }
        }

        setSelectedImage(new_image);
    }

    function previousImage(currentTime, key_used = false) {
        var start_time = false;
        var new_image = {
            index: selectedImage.index,
            image: selectedImage.image_hash,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        };

        if (key_used) {
            new_image.index--;
        }
        else {
            for (var image_index in liveviewData) {
                if (currentTime >= liveviewData[image_index].time_displayed && currentTime <= liveviewData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || new_image.index >= liveviewData.length) {
            if (selectedImage.index == 0 && new_image.index <= -1) {
                new_image.index = 0;
            }
            else {
                if (new_image.index >= liveviewData.length) {
                    var start_time = true;
                }
                new_image.index = liveviewData.length - 1;
            }
        }

        if (typeof liveviewData != 'undefined' && typeof liveviewData[new_image.index] != 'undefined') {
            new_image.image = liveviewData[new_image.index].image_hash;
            new_image.time_displayed = liveviewData[new_image.index].time_displayed;
            new_image.time_changed = liveviewData[new_image.index].time_changed;
        }

        if (key_used) {
            if (start_time) {
                setCurrentTime(startTime);
            }
            else {
                setCurrentTime(Number(new_image.time_displayed) + 1);
            }
        }

        setSelectedImage(new_image);
    }

    useEffect(() => {
        if (directionTrigger?.next > 0) {
            nextImage(null, true);
        }
    }, [directionTrigger?.next]);

    useEffect(() => {
        if (directionTrigger?.prev > 0) {
            previousImage(null, true);
        }
    }, [directionTrigger?.prev]);

    function onMove(moveType) {
        if (moveType == "left") {
            previousImage(null, true);
        }
        else if (moveType == "right") {
            nextImage(null, true);
        }
    }

    function valueLabelFormat(value) {
        return `${moment(value * 1000).format('hh:mmA, DD/MM/YYYY')}`;
    }

    const handleChange = (e, newValue) => {
        if ((newValue && selectedImage.image == null) || (newValue < selectedImage.time_displayed)) {
            previousImage(newValue);
        }
        else if (newValue > selectedImage.time_changed) {
            nextImage(newValue);
        }
        // console.log(selectedImage, newValue)
        setCurrentTime(newValue);
    }

    const datePickerResponsiveProps = {
        textField: {
            variant: "outlined", // Ensure it's outlined to match your screenshots
            sx: {
                '& .MuiInputBase-root': {
                    // Adjust container height
                    height: { xxs: '32px !important', sm: '40px !important', md: 'auto ' },
                    // FORCE the font size change
                    fontSize: {
                        xxs: '12px !important', // Very small for mobile
                        sm: '13px !important',
                        md: '14px'
                    },
                },
                '& .MuiInputBase-input': {
                    // Adjust padding so text doesn't get cut off in a shorter box
                    padding: { xxs: '4px 2px !important', sm: '8px 12px !important' },
                    textAlign: 'center',
                },
                // Shrink the calendar icon button
                '& .MuiInputAdornment-root': {
                    marginLeft: '0px',
                    '& .MuiSvgIcon-root': {
                        display: 'none',
                    }
                },
                // Ensure the width is constrained on mobile
                maxWidth: { xxs: '115px', sm: '160px' }
            }
        }
    };

    const dateSlider = (
        <Card sx={{ minWidth: '100%', border: '1px solid #ccc', backgroundColor: 0, overflow: 'visible' }}>
            {window.innerWidth > breakpoints.values.x5l ? (
                <SoftBox p={1} textAlign="center" display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <SoftBox>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            {isLoading ? (
                                <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled />
                            ) : (
                                <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} />
                            )}

                        </LocalizationProvider>
                    </SoftBox>
                    <SoftBox px={2.5} display="flex" sx={{ height: '100%', width: "100%" }}>
                        <MobileDirectionalPad {...{ onMove, isLoading, currentTime, valueLabelFormat, handleChange, startTime, endTime, deviceLayout }} />
                    </SoftBox>
                    <SoftBox>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            {isLoading ? (
                                <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled />
                            ) : (
                                <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} />
                            )}
                        </LocalizationProvider>
                    </SoftBox>
                </SoftBox>
            ) : (
                <SoftBox px={2} pt={2} textAlign="center">
                    <Grid container>
                        <Grid item xxs={12} mb={1}>
                            <SoftBox display="none" height="100%" sx={{
                                [theme.breakpoints.down('xs')]: {
                                    display: 'flex'
                                }
                            }}>
                                {(device?.overlay_id && device?.overlay_id != 'no_frame') && (
                                    <SoftButton variant="contained" color="dark" fullWidth onClick={() => { setIsPerspectiveLiveview(!isPerspectiveLiveview) }}
                                        sx={{ fontSize: '10px', borderRadius: '8px', minHeight: '32px !important', padding: '0.25rem 1rem !important', lineHeight: '1 !important' }}>
                                        Switch View
                                    </SoftButton>
                                )}
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12}>
                            <SoftBox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled slotProps={deviceLayout == 'mobile' ? datePickerResponsiveProps : {}} />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} slotProps={deviceLayout == 'mobile' ? datePickerResponsiveProps : {}} />
                                        )}

                                    </LocalizationProvider>
                                </SoftBox>
                                <SoftBox display="flex" height="100%" sx={{
                                    [theme.breakpoints.down('xs')]: {
                                        display: 'none'
                                    }
                                }}>
                                    {(device?.overlay_id && device?.overlay_id != 'no_frame') && (
                                        <SoftButton variant="contained" color="dark" circular={true} onClick={() => { setIsPerspectiveLiveview(!isPerspectiveLiveview) }}
                                            sx={{ fontSize: '10px', minHeight: '32px !important', mx: 1 }}>
                                            Switch View
                                        </SoftButton>
                                    )}
                                </SoftBox>
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled slotProps={deviceLayout == 'mobile' ? datePickerResponsiveProps : {}} />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} slotProps={deviceLayout == 'mobile' ? datePickerResponsiveProps : {}} />
                                        )}
                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12}>
                            <MobileDirectionalPad {...{ onMove, isLoading, currentTime, valueLabelFormat, handleChange, startTime, endTime, deviceLayout }} />
                        </Grid>
                    </Grid>
                </SoftBox>
            )
            }
        </Card >
    );

    return (
        <Box display="flex" flex={1} flexDirection={{ xxs: "column" }} height={deviceLayout == 'mobile' ? "calc(100svh - 8.5rem - 16px)" : "calc(100svh - 10.75rem - 16px)"}>
            <Box flex={{ xxs: 1 }} pb={1}>
                <SoftBox display="flex" justifyContent="center" alignItems="center" width="100%" height="auto">
                    {dateSlider}
                </SoftBox>
            </Box>
            <Box display="flex" flex={{ xxs: 11 }}
                sx={{
                    maxHeight: 'calc(100svh - 19rem - 16px)',

                    [breakpoints.up('sm')]: {
                        maxHeight: 'calc(100svh - 16rem - 16px)'
                    },
                    [breakpoints.up('xl')]: {
                        maxHeight: 'calc(100svh - 18rem - 16px)'
                    },
                    [breakpoints.up('x5l')]: {
                        maxHeight: 'calc(100svh - 16rem - 16px)'
                    }
                }}>
                <LiveViewDisplay liveviews={{ url: [selectedImage.image ? selectedImage.image : current_liveview_url] }} {...{ device, deviceLayout, isPerspectiveLiveview }} />
            </Box>
        </Box>
    );
}

// Typechecking props for the LiveviewFeed
LiveviewFeed.propTypes = {
    uuid: PropTypes.string,
    currentTime: PropTypes.number,
    setCurrentTime: PropTypes.func,
    startTime: PropTypes.number,
    setStartTime: PropTypes.func,
    endTime: PropTypes.number,
    setEndTime: PropTypes.func,
    liveviewData: PropTypes.array,
    isLoading: PropTypes.bool,
    current_liveview_url: PropTypes.string,
    device: PropTypes.object,
    deviceLayout: PropTypes.string,
    isPerspectiveLiveview: PropTypes.bool,
    setIsPerspectiveLiveview: PropTypes.func,
    directionTrigger: PropTypes.object
};


export default LiveviewFeed;
